'use client';

import { useCallback, useMemo } from 'react';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { DataLayerEvent, DataLayerEventCategory, isConnectedUserInfo, useUserInfo } from '../../../../src';
import { useDataLayer } from '../dataLayer';
import { useWishlistMutations } from './useWishlistMutations';
import { useWishlistProductsList } from './useWishlistProductsList';

const WishlistDataLayerAction = {
    Add: 'Add to wishlist',
    Remove: 'Remove from wishlist',
};

export type UpdateWishlistParams = {
    // Used to track which product is added or removed from the wishlist. Can be a masterId or a PRODUCT_SET id.
    parentId: string;
    // Used to add or remove a product from the wishlist. Can be a sku or a PRODUCT_SET id.
    productId: string;
    productName?: string;
};

export const useWishlist = () => {
    const { trackEvent } = useDataLayer();

    // Retrieve wishlist and wishlist actions
    const { result: wishlistResult } = useWishlistProductsList();
    const wishlist = unwrapResultSuccess(wishlistResult);

    const { add: addToWishlist, remove: removeFromWishlist } = useWishlistMutations();

    // Retrieve user information
    const { result: userInfoResult } = useUserInfo();
    const isConnected = useMemo(
        () => userInfoResult.type === 'success' && isConnectedUserInfo(userInfoResult.data),
        [userInfoResult],
    );

    const findProductInWishlist = useCallback(
        (productId: string) => {
            return wishlist?.products.find((wishlistProduct) => wishlistProduct.productId === productId);
        },
        [wishlist],
    );

    const update = useCallback(
        ({ productId, productName, parentId }: UpdateWishlistParams) => {
            if (!wishlist) {
                return;
            }

            let wishlistProductsCount = wishlist.products.length;
            const isProductInWishlist = !!findProductInWishlist(productId);

            if (!isProductInWishlist) {
                wishlistProductsCount++;

                void addToWishlist(productId);
            } else {
                wishlistProductsCount--;

                const wishListProductId = findProductInWishlist(productId)?.id;
                if (wishListProductId) {
                    void removeFromWishlist(wishListProductId);
                }
            }
            // Track add delete from/to wishlist event
            trackEvent(DataLayerEvent.Click, {
                id: 'click',
                event_type: 'Wishlist',
                category: DataLayerEventCategory.Wishlist,
                action: !isProductInWishlist ? WishlistDataLayerAction.Add : WishlistDataLayerAction.Remove,
                label: `${productName}|${parentId}`,
                wishlist_number: `${wishlistProductsCount}`,
            });
        },
        [wishlist, findProductInWishlist, trackEvent, addToWishlist, removeFromWishlist],
    );

    return {
        shouldAddToWishlist: isConnected,
        update,
        isProductInWishlist: (productId: string) => !!findProductInWishlist(productId),
    };
};
